import React, { useState, useRef, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import Tesseract from 'tesseract.js';

// OCR 前処理（拡大＋グレースケール＋コントラスト/明るさ）
function enhanceForOCR(dataUrl, { scale = 4.0, contrast = 1.55, brightness = 1.16 } = {}) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const W = Math.round(img.width * scale);
      const H = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = false;
      ctx.filter = `grayscale(1) contrast(${contrast}) brightness(${brightness})`;
      ctx.drawImage(img, 0, 0, W, H);
      resolve(canvas.toDataURL('image/jpeg', 1.0));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

function parseNRHex(text, { minLen = 4, maxLen = 48 } = {}) {
  if (!text) return null;
  let t = text.normalize('NFKC').toUpperCase();
  t = t
    .replace(/\u3000/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  const m = t.match(/\bNR[ \-]*([0-9A-F][0-9A-F \-]*)/);
  if (!m) return null;

  let tail = m[1].replace(/[^0-9A-F \-].*$/, '').trim();

  tail = tail.replace(/ {2,}/g, ' ').trim();

  const compactTail = tail.replace(/[ -]/g, '');
  if (compactTail.length < minLen || compactTail.length > maxLen) return null;

  const groups = tail.split(/[ -]+/).filter(Boolean);
  const pretty = 'NR ' + groups.join(' ');
  const compact = 'NR' + compactTail;

  return { pretty, compact, groups };
}

const TextRead = ({
  disabled = false,
  language = 'eng',
  onResult,
  onProgress,
  autoCloseAfterRead = true,
  className,
  captureIntervalMs = 3000
}) => {
  const [showCamera, setShowCamera] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState('');
  const webcamRef = useRef(null);

  const autoCaptureRequestedRef = useRef(false);
  const hasCapturedRef = useRef(false);

  const captureTimerRef = useRef(null);
  const readingRef = useRef(false);
  const showCameraRef = useRef(false);

  useEffect(() => { readingRef.current = reading; }, [reading]);
  useEffect(() => { showCameraRef.current = showCamera; }, [showCamera]);

  const candidateRef = useRef('');
  const candidateMetaRef = useRef(null);
  const outputDoneRef = useRef(false);

  const stopAutoCapture = useCallback(() => {
    if (captureTimerRef.current) {
      clearInterval(captureTimerRef.current);
      captureTimerRef.current = null;
    }
  }, []);

  const resetConfirmationState = useCallback(() => {
    candidateRef.current = '';
    candidateMetaRef.current = null;
    outputDoneRef.current = false;
  }, []);

  const openCamera = useCallback(() => {
    setError('');
    setOcrProgress(0);
    setShowCamera(true);
    autoCaptureRequestedRef.current = true;
    hasCapturedRef.current = false;
    resetConfirmationState();
  }, [resetConfirmationState]);

  const stopCamera = useCallback(() => {
    setShowCamera(false);
    autoCaptureRequestedRef.current = false;
    stopAutoCapture();
    resetConfirmationState();
  }, [stopAutoCapture, resetConfirmationState]);

  const captureOnceAndRead = useCallback(async () => {
    if (!webcamRef.current) return;

    const tryGetScreenshot = async (retries = 12, delayMs = 150) => {
      for (let i = 0; i < retries; i++) {
        const shot = webcamRef.current?.getScreenshot?.();
        if (shot) return shot;
        await new Promise(r => setTimeout(r, delayMs));
      }
      return null;
    };

    const imageSrc = await tryGetScreenshot();
    if (!imageSrc) {
      setError('画像を取得できませんでした。もう一度お試しください。');
      return;
    }

    setReading(true);
    setError('');
    setOcrProgress(0);

    const psmCandidates = ['11', '6', '3'];

    const totalPasses = psmCandidates.length;
    let found = null;
    let usedMeta = null;

    const recognizeOnce = async (dataUrl, passIndex, psm = '6') => {
      return Tesseract.recognize(dataUrl, language, {
        logger: m => {
          if (m.status === 'recognizing text' && typeof m.progress === 'number') {
            const base = passIndex / totalPasses;
            const p = Math.min(100, Math.round((base + m.progress / totalPasses) * 100));
            setOcrProgress(p);
            onProgress && onProgress(p);
          }
        },
        tessedit_char_whitelist: 'NRABCDEF0123456789 ',
        preserve_interword_spaces: '1',
        tessedit_pageseg_mode: psm,
        user_defined_dpi: '300'
      });
    };

    try {
      const enhanced = await enhanceForOCR(imageSrc, { scale: 3.8, contrast: 1.45, brightness: 1.06 });

      for (let i = 0; i < psmCandidates.length; i++) {
        const psm = psmCandidates[i];
        const result = await recognizeOnce(enhanced, i, psm);
        const fullText = result?.data?.text || '';
        const parsed = parseNRHex(fullText);

        if (parsed) {
          found = parsed;
          usedMeta = {
            roiName: 'full',
            roiRect: { left: 0, top: 0, right: 1, bottom: 1 },
            roiSrc: enhanced,
            psm,
            confidence: result?.data?.confidence,
            fullText
          };
          break;
        }
      }

      if (found && found.compact) {
        const newVal = found.compact;
        const prevVal = candidateRef.current;
        if (!prevVal) {
          candidateRef.current = newVal;
          candidateMetaRef.current = {
            pretty: found.pretty,
            groups: found.groups
          };
        } else if (prevVal === newVal) {
          if (!outputDoneRef.current && onResult) {
            onResult(newVal, {
              pretty: found.pretty,
              groups: found.groups,
              imageSrc,
              ...(usedMeta || {}),
              confirmed: true
            });
            outputDoneRef.current = true;
          }

          if (outputDoneRef.current && autoCloseAfterRead) {
            stopAutoCapture();
            setShowCamera(false);
            autoCaptureRequestedRef.current = false;
          }
        } else {
          candidateRef.current = newVal;
          candidateMetaRef.current = {
            pretty: found.pretty,
            groups: found.groups
          };
        }
      }

    } catch (e) {
      setError(e?.message || 'OCRでエラーが発生しました。');
    } finally {
      setReading(false);
      setOcrProgress(100);
    }
  }, [language, onResult, onProgress, autoCloseAfterRead, stopAutoCapture]);

  const startAutoCapture = useCallback(() => {
    if (captureTimerRef.current) return;
    const interval = Math.max(800, captureIntervalMs || 3000);
    captureTimerRef.current = setInterval(() => {
      if (!showCameraRef.current) return;
      if (readingRef.current) return;
      captureOnceAndRead();
    }, interval);
  }, [captureIntervalMs, captureOnceAndRead]);

  const handleUserMedia = useCallback(() => {
    if (reading) return;
    startAutoCapture();

    if (!autoCaptureRequestedRef.current || hasCapturedRef.current) return;
    hasCapturedRef.current = true;
    setTimeout(() => {
      captureOnceAndRead();
    }, 400);
  }, [reading, startAutoCapture, captureOnceAndRead]);

  useEffect(() => {
    return () => {
      stopAutoCapture();
    };
  }, [stopAutoCapture]);

  return (
    <div className={className}>
      <div className="d-grid gap-2">
        <button
          type="button"
          className="btn btn-secondary w-100 py-2"
          onClick={openCamera}
          disabled={disabled || reading || showCamera}
        >
          カメラを起動
        </button>
        <button
          type="button"
          className="btn btn-secondary w-100 py-2"
          onClick={stopCamera}
          disabled={disabled || reading || !showCamera}
        >
          カメラを閉じる
        </button>
      </div>

      {showCamera && (
        <div className="mt-2">
          <div style={{ position: 'relative' }}>
            <Webcam
              audio={false}
              ref={webcamRef}
              mirrored={false}
              screenshotFormat="image/jpeg"
              screenshotQuality={1}
              className="w-100"
              onUserMedia={handleUserMedia}
              onUserMediaError={(e) => setError(`カメラの起動に失敗しました: ${e?.message || ''}`)}
              videoConstraints={{
                facingMode: 'environment',
                width: 1920,
                height: 1080
              }}
            />
          </div>
          {error && <div className="text-danger mt-2">{error}</div>}
        </div>
      )}
    </div>
  );
};

export default TextRead;
